import React, { useState } from 'react';
import subscriptionLevels from '../data/featuresBySubscriptionLevel.json';

// Keep these plans and feature labels aligned with CleanSweep/app/routes/pricing.
const plans = ['1', '2', '3', '4'].map((key) => subscriptionLevels[key]);
const featureRows = [
  ['Product entry widget', 'productEntryWidget'],
  ['Account widget', 'accountWidget'],
  ['Winner selection', 'winnerSelection'],
  ['Guest lookup widget', 'guestLookupWidget'],
  ['Cart widget', 'cartWidget'],
  ['Bonus spending threshold widget', 'countdownBonusWidget'],
  ['Countdown bonus banner', 'countdownBonusBanner'],
  ['Post-checkout entry summary', 'postCheckoutSummary'],
  ['Membership entry rules', 'memberships'],
  ['Special product and order multipliers', 'specialMultipliers'],
  ['Referral codes for bonus entries', 'referralCodes'],
  ['Email service', 'emailService'],
  ['Award email entries on sign up', 'emailEntryAward'],
  ['Email entry form widget', 'emailDoubleOptInForm'],
  ['Email confirmation toast', 'emailDoubleOptInConfirmRoute'],
  ['Checkout line item entries', 'checkoutExtensionProduct'],
  ['Checkout total entry summary', 'checkoutExtensionTotal'],
  ['Checkout countdown bonus', 'checkoutBonusCountdown'],
  ['Discount codes earn entries', 'discountCodes'],
  ['AMOE tracking', 'amoe'],
];
const highlights = {
  Basic: ['Product entry widget', 'Account widget', 'Winner selection'],
  Gold: ['Everything in Basic', 'Guest lookup and cart widgets', 'Bonus spending threshold widget', 'Countdown bonus banner', 'Post-checkout entry summary'],
  Platinum: ['Everything in Gold', 'Membership entry rules', 'Special product and order multipliers', 'Referral codes for bonus entries', 'Email service and sign-up entries', 'Checkout extensions', 'Discount codes earn entries'],
  Diamond: ['Everything in Platinum', 'AMOE tracking', 'White glove setup service', 'Live meeting to set up your first sweepstakes', 'Sweepstakes consultation services'],
};
const money = (amount) => `$${amount.toLocaleString('en-US')}`;
const orderLimit = (plan) => plan.orderLimit === null ? 'Unlimited' : plan.orderLimit.toLocaleString('en-US');

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="home-pricing">
      <div className="home-section__header">
        <p className="home-eyebrow">Pricing</p>
        <h2>Plans built for every stage of growth.</h2>
        <p className="home-section__lead">Choose the plan that matches your order volume and upgrade anytime.</p>
      </div>
      <div className="pricing-switcher" role="group" aria-label="Billing cycle">
        <button type="button" aria-pressed={!annual} onClick={() => setAnnual(false)}>Monthly</button>
        <button type="button" aria-pressed={annual} onClick={() => setAnnual(true)}>Annual <span>Save 15%</span></button>
      </div>
      <p className="pricing-billing-note" role="status">
        {annual ? 'Annual plans are billed once per year. Prices in USD.' : 'Monthly plans are billed each month. Prices in USD.'}
      </p>
      <div className="home-pricing__grid">
        {plans.map((plan) => (
          <a key={plan.tier} className="pricing-card" href="https://apps.shopify.com/clean-sweeps" target="_blank" rel="noopener noreferrer">
            <h3>{plan.tier}</h3>
            <div className="pricing-amount">
              {annual && <p className="pricing-original"><s>{money(plan.price * 12)}/year</s></p>}
              <p className="price">{money(annual ? Math.floor(plan.price * 12 * 0.85) : plan.price)}<span>/{annual ? 'year' : 'month'}</span></p>
              {annual && <p className="pricing-monthly">Discounted monthly rate: ${(plan.price * 0.85).toFixed(2)}/month</p>}
            </div>
            {plan.tier === 'Platinum' && !annual && <span className="pricing-trial">4-day free trial available</span>}
            <ul>
              <li>{orderLimit(plan)} processed orders per month</li>
              {highlights[plan.tier].map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
            <span className="pricing-plan-link">Get {plan.tier} <span aria-hidden="true">→</span></span>
          </a>
        ))}
      </div>
      <div hidden className="pricing-comparison" role="region" aria-label="Plan feature comparison" tabIndex={0}>
        <table>
          <caption>Compare every plan</caption>
          <thead><tr><th scope="col">Feature</th>{plans.map((plan) => <th scope="col" key={plan.tier}>{plan.tier}</th>)}</tr></thead>
          <tbody>
            <tr><th scope="row">Monthly processed order limit</th>{plans.map((plan) => <td key={plan.tier}>{orderLimit(plan)}</td>)}</tr>
            {featureRows.map(([label, feature]) => (
              <tr key={feature}><th scope="row">{label}</th>{plans.map((plan) => <td key={plan.tier} aria-label={plan.features[feature] ? 'Included' : 'Not included'}><span aria-hidden="true">{plan.features[feature] ? '✓' : '—'}</span></td>)}</tr>
            ))}
            {['White glove support — live meeting to set up your first sweepstakes', 'Sweepstakes consultation services'].map((label) => (
              <tr key={label}><th scope="row">{label}</th>{plans.map((plan) => <td key={plan.tier} aria-label={plan.tier === 'Diamond' ? 'Included' : 'Not included'}><span aria-hidden="true">{plan.tier === 'Diamond' ? '✓' : '—'}</span></td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
